import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Search } from "@/components/icons"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { useNavigatorStore } from "@/stores/navigator-store"

import { CitationCheckboxes } from "./citation-checkboxes"
import { LibraryCheckbox } from "./library-checkbox"
import {
  ACTIVE_CITATIONS_LIBRARY_ITEM_CLASS_NAME,
  filterCitationsGroupedByLibrary,
  groupCitationsByLibrary,
} from "../../utils/active-citations"

type ActiveCitationsProps = {
  sessionType: string
  hideSearchInput?: boolean
  searchQuery?: string
}

export function ActiveCitations({
  sessionType,
  hideSearchInput = false,
  searchQuery,
}: ActiveCitationsProps) {
  const navigatorStore = useNavigatorStore(sessionType)
  const citations = navigatorStore((state) => state.citations)
  const citationLibraryDocuments = navigatorStore((state) => state.citationLibraryDocuments)
  const activeCitations = navigatorStore((state) => state.activeCitations)
  const setActiveCitations = navigatorStore((state) => state.setActiveCitations)
  const citationsGroupedByLibrary = useMemo(
    () => groupCitationsByLibrary(citations, citationLibraryDocuments),
    [citations, citationLibraryDocuments]
  )
  const [localSearchQuery, setLocalSearchQuery] = useState("")
  const effectiveSearchQuery = searchQuery ?? localSearchQuery
  const normalizedQuery = effectiveSearchQuery.trim().toLowerCase()

  const filteredCitationsGroupedByLibrary = useMemo(() => {
    return filterCitationsGroupedByLibrary(citationsGroupedByLibrary, normalizedQuery)
  }, [citationsGroupedByLibrary, normalizedQuery])

  const allCitations = useMemo(() => {
    const entries: string[] = []
    citationsGroupedByLibrary.forEach((libraryCitations) => {
      libraryCitations.forEach((citation) => entries.push(citation.fullName))
    })
    return entries
  }, [citationsGroupedByLibrary])

  const filteredCitations = useMemo(() => {
    const entries: string[] = []
    filteredCitationsGroupedByLibrary.forEach((libraryCitations) => {
      libraryCitations.forEach((citation) => entries.push(citation.fullName))
    })
    return entries
  }, [filteredCitationsGroupedByLibrary])

  const activeCount = useMemo(() => {
    const available = new Set(allCitations)
    return activeCitations.filter((citation) => available.has(citation)).length
  }, [activeCitations, allCitations])

  const activeSet = useMemo(() => new Set(activeCitations), [activeCitations])
  const filteredCount = filteredCitations.length
  const totalCount = allCitations.length

  const canSelectAll = useMemo(
    () => filteredCitations.some((citation) => !activeSet.has(citation)),
    [filteredCitations, activeSet]
  )
  const canClear = useMemo(
    () => filteredCitations.some((citation) => activeSet.has(citation)),
    [filteredCitations, activeSet]
  )

  const { t } = useTranslation("", { keyPrefix: "settingsmenu" })
  const countLabel = t("active_citations_count", { active: activeCount, total: totalCount })
  const showingLabel = t("active_citations_showing_count", {
    shown: filteredCount,
    total: totalCount,
  })
  const countBadgeClassName = cn(
    "shrink-0 font-semibold",
    activeCount > 0
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-border bg-muted/40 text-muted-foreground"
  )

  function handleSelectAll() {
    if (filteredCitations.length === 0) return
    const merged = new Set(activeCitations)
    filteredCitations.forEach((citation) => merged.add(citation))
    setActiveCitations(Array.from(merged))
  }

  function handleClear() {
    if (filteredCitations.length === 0) return
    const filteredSet = new Set(filteredCitations)
    setActiveCitations(activeCitations.filter((citation) => !filteredSet.has(citation)))
  }

  return (
    <section className="flex h-full min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t("active_citations")}
          </span>
          <p className="text-muted-foreground text-xs">{t("active_citations_description")}</p>
        </div>
        <Badge
          aria-label={countLabel}
          className={countBadgeClassName}
          title={countLabel}
          variant="outline"
        >
          {activeCount}/{totalCount}
        </Badge>
      </div>

      {totalCount === 0 ? (
        <p className="text-muted-foreground text-sm">{t("no_citations")}</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {!hideSearchInput && (
              <div className="relative">
                <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
                <Input
                  className="h-9 pl-9"
                  placeholder={t("active_citations_search_placeholder")}
                  value={effectiveSearchQuery}
                  onChange={(event) => setLocalSearchQuery(event.target.value)}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-muted-foreground text-xs">{showingLabel}</span>
              <div className="flex items-center gap-2">
                <Button
                  disabled={!canSelectAll}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={handleSelectAll}
                >
                  {t("active_citations_select_all")}
                </Button>
                <Button
                  disabled={!canClear}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={handleClear}
                >
                  {t("active_citations_clear")}
                </Button>
              </div>
            </div>
          </div>

          {filteredCount === 0 ? (
            <p className="text-muted-foreground text-sm">{t("active_citations_no_results")}</p>
          ) : (
            <Accordion
              key={normalizedQuery}
              className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto"
              defaultValue={Array.from(filteredCitationsGroupedByLibrary.keys())}
              type="multiple"
            >
              {Array.from(filteredCitationsGroupedByLibrary.entries()).map(
                ([library, libraryCitations]) => (
                  <AccordionItem
                    key={library}
                    className={ACTIVE_CITATIONS_LIBRARY_ITEM_CLASS_NAME}
                    value={library}
                  >
                    <AccordionTrigger className="[&>svg]:text-muted-foreground items-start gap-2 py-2 hover:no-underline [&>svg]:mt-1 [&>svg]:size-4 [&>svg]:shrink-0">
                      <LibraryCheckbox
                        citationsGroupedByLibrary={filteredCitationsGroupedByLibrary}
                        library={library}
                        sessionType={sessionType}
                      />
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-2">
                      <CitationCheckboxes
                        libraryCitations={libraryCitations}
                        libraryName={library}
                        searchQuery={effectiveSearchQuery}
                        sessionType={sessionType}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )
              )}
            </Accordion>
          )}
        </>
      )}
    </section>
  )
}
